import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const UnsavedChangesContext = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const [isDirty, setIsDirty] = useState(false);
  const saveRef = useRef(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    const handler = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const setUnsavedChanges = useCallback((dirty, onSave) => {
    setIsDirty(!!dirty);
    saveRef.current = onSave || null;
  }, []);

  const requestConfirm = useCallback(
    (onProceed) => {
      if (!isDirty) {
        onProceed?.();
        return;
      }
      setPending({ onProceed, onCancel: () => setPending(null) });
    },
    [isDirty]
  );

  const handleSave = useCallback(async () => {
    if (saveRef.current) {
      const ok = await saveRef.current();
      if (ok === false) return;
    }
    setIsDirty(false);
    const proceed = pending?.onProceed;
    setPending(null);
    proceed?.();
  }, [pending]);

  const handleDiscard = useCallback(() => {
    setIsDirty(false);
    const proceed = pending?.onProceed;
    setPending(null);
    proceed?.();
  }, [pending]);

  const handleCancel = useCallback(() => {
    pending?.onCancel?.();
    setPending(null);
  }, [pending]);

  return (
    <UnsavedChangesContext.Provider value={{ setUnsavedChanges, requestConfirm }}>
      {children}
      {pending && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "grid",
            placeItems: "center",
            zIndex: 2000,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(420px, 92vw)",
              background: "var(--card)",
              color: "var(--ink)",
              border: "1px solid var(--stroke)",
              borderRadius: 14,
              padding: 18,
              boxShadow: "0 16px 38px rgba(0,0,0,0.25)",
              display: "grid",
              gap: 12,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 16 }}>You have unsaved changes</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              Save your changes before leaving this page?
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={handleCancel}>Cancel</button>
              <button onClick={handleDiscard} style={{ background: "#8f9aa3" }}>
                Discard
              </button>
              <button onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error("useUnsavedChanges must be used within UnsavedChangesProvider");
  }
  return ctx;
}
